/**
 * PwaUpdateContext.jsx — FULLY AUTOMATIC PWA updates.
 * ============================================================================
 *
 * Primary flow (no user action required):
 *   1. Register sw.js with updateViaCache:'none' so the SW script is always
 *      revalidated over the network (no 24h HTTP-cache trap).
 *   2. Run registration.update() on load + focus + resume (visibilitychange)
 *      + a periodic interval.
 *   3. When a new build is detected (waiting / installed worker), automatically
 *      post {type:'SKIP_WAITING'} so it activates, then reload the page on
 *      `controllerchange`. A short "Laddar om…" banner is shown briefly before
 *      the reload — purely informational, no button.
 *
 * Safety:
 *   - We defer the auto-activation/reload while a SENSITIVE flow is active
 *     (running test / live match) to avoid losing in-progress state. As soon
 *     as the flow ends the update applies automatically. Deploys happen late
 *     evening when this is practically never the case.
 *
 * Fallback:
 *   - If the automatic reload cannot complete for technical reasons, a small
 *     banner with a "Ladda om" button is shown as a backup. This is NOT the
 *     primary flow.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

const PwaUpdateContext = createContext(null);

export const usePwaUpdate = () => useContext(PwaUpdateContext);

export function PwaUpdateProvider({ children }) {
  // 'idle' | 'reloading' (brief auto banner) | 'fallback' (manual backup)
  const [status, setStatus] = useState('idle');
  const [sensitiveActive, setSensitiveActive] = useState(false);

  const waitingWorkerRef = useRef(null);
  const sensitiveCountRef = useRef(0);
  const reloadingRef = useRef(false);
  const pendingUpdateRef = useRef(false); // update detected but deferred (sensitive)
  const pendingReloadRef = useRef(false); // controllerchange fired but deferred

  const doReload = useCallback(() => {
    if (sensitiveCountRef.current > 0) { pendingReloadRef.current = true; return; }
    if (reloadingRef.current) return;
    // Reload-loop protection: never auto-reload twice within a short window.
    // If we just reloaded, surface the manual fallback instead of looping.
    try {
      const now = Date.now();
      const last = parseInt(window.sessionStorage.getItem('pwa-last-reload') || '0', 10);
      if (last && now - last < 20000) {
        setStatus('fallback');
        return;
      }
      window.sessionStorage.setItem('pwa-last-reload', String(now));
    } catch (e) {
      // sessionStorage unavailable (private mode etc.) — proceed with reload.
    }
    reloadingRef.current = true;
    window.location.reload();
  }, []);

  const startAutoUpdate = useCallback(() => {
    if (sensitiveCountRef.current > 0) { pendingUpdateRef.current = true; return; }
    setStatus('reloading'); // show brief "Laddar om…" banner
    const worker = waitingWorkerRef.current;
    try {
      if (worker) {
        worker.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch (e) {
      // If we cannot message the worker, fall back to a plain reload below.
    }
    // `controllerchange` normally triggers the reload. Force it shortly after
    // as a safety net so the user always lands on the newest build.
    setTimeout(() => doReload(), worker ? 1800 : 1000);
    // Watchdog: if the page is still alive after the reload attempt, the reload
    // was prevented for some technical reason — surface the manual backup button.
    setTimeout(() => {
      reloadingRef.current = false;
      setStatus((s) => (s === 'reloading' ? 'fallback' : s));
    }, 7000);
  }, [doReload]);

  const triggerUpdate = useCallback((worker) => {
    if (worker) waitingWorkerRef.current = worker;
    if (sensitiveCountRef.current > 0) {
      pendingUpdateRef.current = true; // apply automatically once flow ends
      return;
    }
    startAutoUpdate();
  }, [startAutoUpdate]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    let registration;
    let cleanup = () => {};

    const onControllerChange = () => {
      if (sensitiveCountRef.current > 0) {
        pendingReloadRef.current = true; // reload as soon as flow ends
        return;
      }
      doReload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    const register = async () => {
      try {
        registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none',
        });

        // Proactively check for a newer SW immediately on this load.
        registration.update().catch(() => {});

        // A new build was already installed & waiting from a previous session.
        if (registration.waiting && navigator.serviceWorker.controller) {
          triggerUpdate(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            // `installed` + an existing controller = an update (not first install).
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              triggerUpdate(newWorker);
            }
          });
        });

        // Check for updates on focus, on resume (tab/app becomes visible),
        // and periodically — covers iOS PWA, Safari, Chrome, installed mode.
        const check = () => registration && registration.update().catch(() => {});
        const onFocus = () => check();
        const onVisible = () => {
          if (document.visibilityState === 'visible') check();
        };
        const interval = setInterval(check, 15 * 60 * 1000);
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisible);
        cleanup = () => {
          clearInterval(interval);
          window.removeEventListener('focus', onFocus);
          document.removeEventListener('visibilitychange', onVisible);
        };
      } catch (e) {
        // Registration failed — app still works, just without auto-update.
      }
    };

    if (document.readyState === 'complete') {
      register();
    } else {
      window.addEventListener('load', register, { once: true });
    }

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      cleanup();
    };
  }, [doReload, triggerUpdate]);

  // When a sensitive flow ends, apply any deferred update/reload automatically.
  useEffect(() => {
    if (sensitiveActive) return;
    if (pendingReloadRef.current) {
      pendingReloadRef.current = false;
      doReload();
      return;
    }
    if (pendingUpdateRef.current && status === 'idle') {
      pendingUpdateRef.current = false;
      startAutoUpdate();
    }
  }, [sensitiveActive, status, startAutoUpdate, doReload]);

  // Manual backup (only used by the fallback banner button).
  const applyUpdate = useCallback(() => {
    const worker = waitingWorkerRef.current;
    try {
      if (worker) worker.postMessage({ type: 'SKIP_WAITING' });
    } catch (e) { /* ignore */ }
    doReload();
  }, [doReload]);

  // Surface a fallback banner only if an update was detected but the automatic
  // reload could not complete (handled by the watchdog in startAutoUpdate).

  const registerSensitive = useCallback(() => {
    sensitiveCountRef.current += 1;
    setSensitiveActive(sensitiveCountRef.current > 0);
    return () => {
      sensitiveCountRef.current = Math.max(0, sensitiveCountRef.current - 1);
      setSensitiveActive(sensitiveCountRef.current > 0);
    };
  }, []);

  const value = {
    status,
    showReloadingBanner: status === 'reloading',
    showFallbackBanner: status === 'fallback',
    applyUpdate,
    registerSensitive,
  };

  return <PwaUpdateContext.Provider value={value}>{children}</PwaUpdateContext.Provider>;
}

/**
 * useSensitiveFlow(active) — mark the current screen as a sensitive flow while
 * `active` is true. Defers automatic update/reload until the flow ends.
 */
export function useSensitiveFlow(active) {
  const ctx = useContext(PwaUpdateContext);
  const register = ctx?.registerSensitive;
  useEffect(() => {
    if (!active || !register) return undefined;
    const unregister = register();
    return unregister;
  }, [active, register]);
}

export default PwaUpdateContext;
